import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { PAYMENT_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payment-coupons`;

const normalizeCouponPayload = (payload = {}) => {
  const {
    app,
    startsAt,
    expiresAt,
    minAmount,
    minimumAmount,
    validRange,
    ...rest
  } = payload;
  const discountType =
    payload.discountType === "FIXED" ? "FIXED_AMOUNT" : payload.discountType;
  const fixedAmount =
    discountType === "FIXED_AMOUNT"
      ? payload.amount ?? payload.discountValue
      : payload.amount;

  return {
    ...rest,
    discountType,
    amount: fixedAmount,
    duration: payload.duration || "ONE_TIME",
    clientApp: payload.clientApp || app,
    chargeItemCode: payload.chargeItemCode || payload.chargeCode,
    validFrom: payload.validFrom || startsAt,
    validTo: payload.validTo || expiresAt,
    minChargeAmount: payload.minChargeAmount ?? minAmount ?? minimumAmount,
  };
};

const normalizePagedResponse = (payload, page, size) => {
  const content = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.content)
      ? payload.content
      : [];

  return {
    content,
    meta: Array.isArray(payload)
      ? {
          page,
          number: page,
          size,
          totalElements: content.length,
          totalPages: 1,
        }
      : {
          ...payload,
          page: payload?.page ?? payload?.number ?? page,
          number: payload?.number ?? payload?.page ?? page,
          size: payload?.size ?? size,
          totalElements: payload?.totalElements ?? content.length,
          totalPages: payload?.totalPages ?? 1,
        },
  };
};

export default function usePaymentCoupons() {
  const authToken = sessionStorage.getItem("authToken");
  const [coupons, setCoupons] = useState([]);
  const [meta, setMeta] = useState({
    page: 0,
    number: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [usagesLoading, setUsagesLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAxiosErrors = (err, action = "operation") => {
    const message = err.response
      ? err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`
      : err.request
        ? "No response from the server. Please check your internet connection."
        : err.message || "An unexpected error occurred.";

    toast.error(message);
    console.error(`${action} error`, err);
  };

  const fetchCoupons = useCallback(
    async ({ page = 0, size = 20, search, status } = {}) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = { page, size };
        if (search) params.code = search;
        if (status) params.status = status;

        const res = await apiClient.get(BASE_URL, { params });
        const normalized = normalizePagedResponse(res.data, page, size);

        setCoupons(normalized.content);
        setMeta(normalized.meta);

        return normalized;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch coupons");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const createCoupon = useCallback(
    async (payload) => {
      if (!authToken) return;

      setLoading(true);
      try {
        const res = await apiClient.post(BASE_URL, normalizeCouponPayload(payload));
        toast.success("Coupon created successfully");
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "create coupon");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const fetchCouponUsages = useCallback(
    async ({ page = 0, size = 500 } = {}) => {
      if (!authToken) return normalizePagedResponse([], page, size);

      setUsagesLoading(true);
      try {
        const res = await apiClient.get(`${BASE_URL}/usages`, {
          params: { page, size },
        });
        return normalizePagedResponse(res.data, page, size);
      } catch (err) {
        handleAxiosErrors(err, "fetch coupon usages");
        throw err;
      } finally {
        setUsagesLoading(false);
      }
    },
    [authToken],
  );

  const getCouponByCode = useCallback(
    async (code) => {
      const normalizedCode = String(code || "").trim().toUpperCase();
      if (!authToken || !normalizedCode) return null;

      try {
        const res = await apiClient.get(`${BASE_URL}/code/${normalizedCode}`);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch coupon details");
        throw err;
      }
    },
    [authToken],
  );

  const checkCouponCode = useCallback(
    async (code, currentCouponId) => {
      const normalizedCode = String(code || "").trim().toUpperCase();
      if (!authToken || !normalizedCode) return { available: true };

      try {
        const res = await apiClient.get(BASE_URL, {
          params: {
            page: 0,
            size: 10,
            code: normalizedCode,
            search: normalizedCode,
          },
        });
        const { content } = normalizePagedResponse(res.data, 0, 10);
        const matchingCoupon = content.find(
          (item) => String(item?.code || "").toUpperCase() === normalizedCode,
        );

        if (!matchingCoupon) return { available: true };

        const matchingCouponId =
          matchingCoupon.id || matchingCoupon.couponId || matchingCoupon.code;
        const isCurrentCoupon =
          currentCouponId &&
          String(matchingCouponId || "").toUpperCase() ===
            String(currentCouponId).toUpperCase();

        return {
          available: Boolean(isCurrentCoupon),
          coupon: matchingCoupon,
        };
      } catch (err) {
        handleAxiosErrors(err, "check coupon code");
        throw err;
      }
    },
    [authToken],
  );

  const updateCoupon = useCallback(
    async (couponId, payload) => {
      if (!authToken) return;

      setLoading(true);
      try {
        const res = await apiClient.patch(
          `${BASE_URL}/${couponId}`,
          normalizeCouponPayload(payload),
        );
        toast.success("Coupon updated successfully");
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "update coupon");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const toggleCouponStatus = useCallback(
    async (couponId, status) => {
      if (!authToken) return;

      setLoading(true);
      try {
        const res = await apiClient.patch(`${BASE_URL}/${couponId}/status`, {
          status,
        });
        toast.success(
          `Coupon ${status === "ACTIVE" ? "activated" : "deactivated"}`,
        );
        setCoupons((prev) =>
          prev.map((coupon) =>
            coupon.id === couponId ||
            coupon.couponId === couponId ||
            coupon.code === couponId
              ? { ...coupon, status: res.data?.status ?? status }
              : coupon,
          ),
        );
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "update coupon status");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  return {
    coupons,
    meta,
    loading,
    usagesLoading,
    error,
    fetchCoupons,
    fetchCouponUsages,
    getCouponByCode,
    checkCouponCode,
    createCoupon,
    updateCoupon,
    toggleCouponStatus,
  };
}
